/**
 * Predicted marking time of a marking file, as the backend reports it.
 *
 * The backend predicts it with the laser agent's model (colorExplorer/
 * marking_time.py): marking length at each group's speed, the jumps at the
 * jump speed, plus the scanner's delay terms per vector. Pages get it as a
 * ``{seconds, text}`` payload on layouts and rows, or in the
 * X-Marking-Time-Seconds / X-Marking-Time-Text headers of a file response.
 */

/** "4 min 12 s", "1 h 02 min", "12 s", "< 1 s"; null without a value. The
 *  backend's format, for values that only come as seconds (the agent's). */
export function formatMarkingTime(seconds) {
    const value = Number(seconds);
    if (seconds === null || seconds === undefined || !Number.isFinite(value)) return null;
    const clamped = Math.max(0, value);
    if (clamped < 1) return "< 1 s";
    const total = Math.round(clamped);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    const two = (n) => String(n).padStart(2, "0");
    if (hours) return `${hours} h ${two(minutes)} min`;
    if (minutes) return `${minutes} min ${two(secs)} s`;
    return `${secs} s`;
}

/** The ``{seconds, text}`` payload of a value that may be a payload already,
 *  a number of seconds, or nothing. */
export function markingTimeValue(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "object") {
        const seconds = Number(value.seconds);
        if (!Number.isFinite(seconds)) return null;
        return { seconds, text: value.text || formatMarkingTime(seconds) };
    }
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return null;
    return { seconds, text: formatMarkingTime(seconds) };
}

/** The prediction of a file response, from its X-Marking-Time headers. */
export function markingTimeFromHeaders(headers) {
    if (!headers) return null;
    const get = (name) => headers[name] ?? headers[name.toLowerCase()] ?? (typeof headers.get === "function" ? headers.get(name) : undefined);
    const raw = get("X-Marking-Time-Seconds");
    if (raw === undefined || raw === null || raw === "") return null;
    const seconds = Number(raw);
    if (!Number.isFinite(seconds)) return null;
    return { seconds, text: get("X-Marking-Time-Text") || formatMarkingTime(seconds) };
}

/** The sum of several predictions, null when any of them is missing. */
export function sumMarkingTimes(values) {
    const payloads = (values || []).map(markingTimeValue);
    if (payloads.length === 0 || payloads.some((p) => p === null)) return null;
    const seconds = payloads.reduce((total, p) => total + p.seconds, 0);
    return { seconds, text: formatMarkingTime(seconds) };
}
